import {FC, useMemo, useRef} from 'react';
import {Drawer} from 'shared/ui/drawer';
import {useCalendarNewEvent} from '../lib/useCalendarNewEvent';
import {
  NewEventBody,
  NewEventBorder,
  NewEventFooter,
  NewEventHead,
  NewEventRow,
  NewEventTextAreaWrapper,
  NewEventTitle,
  NewEventWrapper,
} from './styles';
import {Spacer} from 'shared/ui/spacer';
import {toRem} from 'shared/helpers';
import {CloseIcon} from 'shared/ui/icons';
import {Controller} from 'react-hook-form';
import {NewEventFormValues} from 'shared/types';
import {useApiariesStore} from 'entities/apiaries';

import Multiselect from '@atomaro/ui-kit/components/Multiselect/Multiselect';
import Button from '@atomaro/ui-kit/components/Button/Button/Button';
import InputDate from '@atomaro/ui-kit/components/InputDate/InputDate';
import Switch from '@atomaro/ui-kit/components/Switch/Switch';
import Select from '@atomaro/ui-kit/components/Select/Select';
import {withMask} from 'use-mask-input';
import {getEndAt, getStartAt} from '../helpers/dates';
import {IDropdownMenuItem} from '@atomaro/ui-kit/components/DropdownMenu/types';

type CalendarNewEventProps = {
  isOpen: boolean;
  isMobile?: boolean;
  editId?: string | number;
  defaultValues?: Partial<NewEventFormValues>;
  isHivesDisabled?: boolean;
  onClose: () => void;
  onSubmit?: (data: NewEventFormValues) => void;
  selectedHives?: IDropdownMenuItem[];
};
export const CalendarNewEvent: FC<CalendarNewEventProps> = ({
  isOpen,
  isMobile,
  editId,
  onClose,
  onSubmit,
  defaultValues = {
    hiveIds: [],
    hives: [],
    description: '',
    startAt: getStartAt(),
    endAt: getEndAt(),
    notifyAt: undefined,
    notifyAtRepeat: undefined,
    diaryEntryId: undefined,
  },
  selectedHives,
}) => {
  const {
    isLoading,
    hives,
    control,
    noteHeight,
    notifySelectItems,
    isEndReadOnly,
    endAtMinDate,
    notifyAtDefaultValue,
    notifyAtRepeatDefaultValue,
    errors,
    onHivesChange,
    onDescriptionChange,
    handleSubmit,
    // onSubmit,
    onStartDateChange,
    onEndDateChange,
    onAllDayChange,
    onNotifyAtChange,
    onnotifyAtRepeatChange,
    reset,
    onSubmitDebounced,
  } = useCalendarNewEvent({defaultValues, editId, onClose});

  const currentApiaryId = useApiariesStore((s) => s.currentApiaryId);

  const hiveItems = useMemo(() => {
    if (!currentApiaryId) return [];
    const filteredHives = hives.filter((hive) => hive.apiary?.id === currentApiaryId);
    return filteredHives.map((hive) => ({value: `№${hive.number}`, key: hive.id}));
  }, [hives, currentApiaryId]);

  const selectRef = useRef<HTMLInputElement | null>(null);

  const onSelectFocus = () => {
    if (!selectRef.current) return;

    selectRef.current.setAttribute('autocomplete', 'off');
  };

  const onReset = () => {
    onClose();
    reset();
  };

  const endAtMinDateDate = useMemo(() => {
    return new Date(new Date(endAtMinDate).setSeconds(0, 0));
  }, [endAtMinDate]);

  return (
    <Drawer isOpen={isOpen} position="right" onClose={onClose}>
      <NewEventWrapper
        className={isLoading ? '_skeleton' : ''}
        onSubmit={handleSubmit(onSubmit ? onSubmit : onSubmitDebounced)}
      >
        <NewEventHead $isMobile={isMobile}>
          <Button
            iconPrefix={<CloseIcon />}
            variant={isMobile ? 'ghost' : 'secondary'}
            colorScheme="neutral"
            style={{alignSelf: 'flex-end', width: isMobile ? toRem(24) : toRem(36)}}
            size={isMobile ? 's' : 'm'}
            onClick={onClose}
          />
          <NewEventTitle>{editId ? 'Редактирование события' : 'Новое событие'}</NewEventTitle>
          {isMobile && <Spacer width={toRem(24)} height={toRem(24)} />}
        </NewEventHead>
        <NewEventBody>
          <NewEventRow>
            <span>Номер улья</span>
            <Controller
              control={control}
              name="hives"
              render={({field}) => (
                <>
                  <Multiselect
                    placeholder={
                      selectedHives && selectedHives?.length > 0 ? undefined : 'Все ульи'
                    }
                    size="m"
                    value={selectedHives}
                    name={field.name}
                    items={hiveItems}
                    dropdownMenuStyle={{
                      maxHeight: toRem(300),
                      overflowY: 'auto',
                    }}
                    ref={selectRef}
                    onFocus={onSelectFocus}
                    onChange={onHivesChange}
                    autocomplete={{enabled: true}}
                    useInPortal={false}
                    error={errors.hiveIds?.message ? errors.hiveIds.message : ''}
                    autoHeight={true}
                  />
                </>
              )}
            />
          </NewEventRow>
          {/* {!editId && 
          } */}
          <NewEventRow $alignItems="flex-start">
            <span>Описание</span>
            <Controller
              name="description"
              control={control}
              rules={{maxLength: {value: 2000, message: 'Не больше 2000 символов'}}}
              render={({field}) => (
                <NewEventTextAreaWrapper $height={noteHeight}>
                  <textarea
                    name={field.name}
                    value={field.value}
                    onChange={(event) => onDescriptionChange(event, field)}
                  />
                </NewEventTextAreaWrapper>
              )}
            />
          </NewEventRow>

          <NewEventBorder />

          <NewEventRow>
            <span>Начало</span>
            <Controller
              name="startAt"
              control={control}
              render={({field}) => (
                <InputDate
                  minDate={getStartAt()}
                  activeDate={field.value}
                  minYear={new Date().getFullYear()}
                  onChange={onStartDateChange}
                  showTime
                  ref={withMask('99.99.9999 99:99')}
                />
              )}
            />
          </NewEventRow>
          <NewEventRow>
            <span>Окончание</span>
            <Controller
              name="endAt"
              control={control}
              render={({field}) => (
                <InputDate
                  minDate={endAtMinDateDate}
                  activeDate={field.value}
                  minYear={new Date().getFullYear()}
                  onChange={onEndDateChange}
                  showTime
                  disabled={isEndReadOnly}
                />
              )}
            />
          </NewEventRow>
          <NewEventRow>
            <span>Весь день</span>
            <Switch onChange={onAllDayChange} />
          </NewEventRow>

          <NewEventBorder />

          <NewEventRow>
            <span>Напоминание</span>
            <Controller
              control={control}
              name="notifyAtNum"
              render={({field}) => (
                <Select
                  items={notifySelectItems}
                  name={field.name}
                  value={field.value || 'null'}
                  dropdownMenuStyle={{
                    maxHeight: toRem(300),
                    overflowY: 'auto',
                  }}
                  placement="bottom"
                  defaultValue={notifyAtDefaultValue}
                  onChange={onNotifyAtChange}
                  useInPortal={false}
                />
              )}
            />
          </NewEventRow>
          <NewEventRow>
            <span>2-е напоминание</span>
            <Controller
              control={control}
              name="notifyAtRepeatNum"
              render={({field}) => (
                <Select
                  items={notifySelectItems}
                  name={field.name}
                  value={field.value || 'null'}
                  dropdownMenuStyle={{
                    maxHeight: toRem(300),
                    overflowY: 'auto',
                  }}
                  placement="bottom"
                  defaultValue={notifyAtRepeatDefaultValue}
                  onChange={onnotifyAtRepeatChange}
                  useInPortal={false}
                />
              )}
            />
          </NewEventRow>
        </NewEventBody>
        <NewEventFooter>
          <Button type="submit" label={editId ? 'Сохранить' : 'Создать запись'} />
          <Button label="Отменить" variant="ghost" colorScheme="neutral" onClick={onReset} />
        </NewEventFooter>
      </NewEventWrapper>
    </Drawer>
  );
};
